import { useEffect, useState } from "react";
import {
    Box, Button, Typography, Paper, Stack, IconButton, Tooltip
} from "@mui/material";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertConfirm, AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import ModalCategoriaPagar from "./ModalCategoriaPagar";

export type CategoriaPagar = {
    id_categoria_pagar: number;
    id_escritorio_categoria: number;
    nome_categoria: string;
    categoria_ativa: number;
    create_categoria: string;
    update_categoria: string;
};

export default function TabCategoriasPagar() {
    const [openLoad, setOpenLoad] = useState(false);
    const [categorias, setCategorias] = useState<CategoriaPagar[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [editando, setEditando] = useState<CategoriaPagar | null>(null);

    async function loadCategorias() {
        setOpenLoad(true);
        try {
            const res = await api.get('/categoriaspagar');
            if (Array.isArray(res.data)) setCategorias(res.data);
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha ao carregar categorias' });
        }
        setOpenLoad(false);
    }

    async function handleDeletar(categoria: CategoriaPagar) {
        const confirm = await AlertConfirm({
            title: `Deseja realmente excluir a categoria "${categoria.nome_categoria}"?`,
        });
        if (!confirm.isConfirmed) return;

        setOpenLoad(true);
        try {
            const r = await api.delete(`/deletecategoriapagar/${categoria.id_categoria_pagar}`);
            if (r.data.success) {
                AlertInfo({ icon: 'success', title: r.data.msg || 'Categoria removida!' });
                loadCategorias();
            } else {
                AlertInfo({ icon: 'error', title: r.data.msg || 'Falha ao remover' });
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    function handleEditar(categoria: CategoriaPagar) {
        setEditando(categoria);
        setOpenModal(true);
    }

    function handleNovo() {
        setEditando(null);
        setOpenModal(true);
    }

    useEffect(() => { loadCategorias(); }, []);

    return (
        <>
            <SimpleBackdrop open={openLoad} />

            <ModalCategoriaPagar
                open={openModal}
                onClose={() => { setOpenModal(false); setEditando(null); }}
                onSaved={loadCategorias}
                categoria={editando}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Categorias de Contas a Pagar</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Cadastre as categorias usadas na classificação das despesas do escritório
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon />}
                    onClick={handleNovo}
                    sx={{ textTransform: 'none' }}
                >
                    Nova Categoria
                </Button>
            </Stack>

            {categorias.length === 0 && !openLoad && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <LocalOfferIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                        Nenhuma categoria cadastrada
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        Crie sua primeira categoria clicando no botão acima
                    </Typography>
                </Box>
            )}

            <Stack spacing={1.5}>
                {categorias.map(categoria => (
                    <Paper
                        key={categoria.id_categoria_pagar}
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: '#fff',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: 2 },
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <LocalOfferIcon color="primary" sx={{ fontSize: 22 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                    {categoria.nome_categoria}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.3}>
                                <Tooltip title="Editar">
                                    <IconButton size="small" color="primary" onClick={() => handleEditar(categoria)}>
                                        <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                    <IconButton size="small" color="error" onClick={() => handleDeletar(categoria)}>
                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        </>
    );
}
